import { requestApi } from "../api/client.js";
import type { AssignmentChange } from "./setup-checklist-model.js";

export interface PlanCheck {
  // Due for everyone at the location, following people as they join.
  assign_everyone?: boolean;
  assignee_staff_profile_id: string | null;
  assignee_staff_profile_ids: string[];
  id: string;
  origin: "manager" | "plan_generated";
  schedule: {
    due_window_start: string | null;
    one_off_due_at: string | null;
    rrule: string | null;
  } | null;
  status: "active" | "archived" | "paused";
  title: string;
}

type Page = { items: PlanCheck[]; next_cursor: string | null };

// Every check that is not archived, across pages. Manager-made checks need
// people too, and assigning a plan check can mark it manager-made.
export async function listPlanChecks(locationId: string): Promise<PlanCheck[]> {
  const checks: PlanCheck[] = [];
  let cursor: string | null = null;
  do {
    const query = new URLSearchParams({
      limit: "200",
      location_id: locationId,
    });
    if (cursor) query.set("cursor", cursor);
    const page: Page = await requestApi<Page>(`/task-definitions?${query}`);
    checks.push(...page.items.filter((task) => task.status !== "archived"));
    cursor = page.next_cursor;
  } while (cursor);
  return checks;
}

export function assignCheck(change: AssignmentChange): Promise<PlanCheck> {
  return requestApi<PlanCheck>(`/task-definitions/${change.id}`, {
    body: JSON.stringify(
      "assign_everyone" in change
        ? { assign_everyone: true }
        : { assignee_staff_profile_ids: change.assignee_staff_profile_ids },
    ),
    method: "PATCH",
  });
}

const BATCH = 4;

// Applies each change; a failure never stops the rest.
export async function assignChecks(
  changes: readonly AssignmentChange[],
): Promise<{ failed: number; failedIds: string[]; saved: number }> {
  const failedIds: string[] = [];
  let saved = 0;
  for (let index = 0; index < changes.length; index += BATCH) {
    const batch = changes.slice(index, index + BATCH);
    const results = await Promise.allSettled(
      batch.map((change) => assignCheck(change)),
    );
    results.forEach((result, position) => {
      if (result.status === "fulfilled") saved += 1;
      else failedIds.push(batch[position]!.id);
    });
  }
  return { failed: failedIds.length, failedIds, saved };
}
